/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, FormControl } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { StyledSwitch } from "../total_sales_card/styles";
import { StyledMenuItem } from "../styled_menu_item";
import { SelectedMenuItem } from "../selected_menu_item";
import { DropdownInfiniteLoader } from "../dropdown_infinite_loader";
import DropdownSearchBar from "../dorpdown_searchbar";
import {
  ACTIVE_POLICY_CHECKING_TEXT,
  ACTIVE_POLICY_CLEAR_TEXT,
  APPLIES_TO_OPTIONS,
  DEFAULT_INACTIVITY_CHECKS,
  DEFAULT_INACTIVITY_PERIOD_DAYS,
  hasAnyInactivityReviewField,
  INACTIVITY_CHECK_OPTIONS,
  isIntegerFieldSet,
  NOTIFICATION_RECIPIENT_OPTIONS,
  sanitizeIntegerInput,
  VERIFICATION_FREQUENCY_OPTIONS,
} from "./policy_configuration";
import {
  ActivePolicyCheckText,
  ActivityChip,
  ActivityChipCheckbox,
  ActivityChipGroup,
  AddRuleButton,
  CloseIconButton,
  dropdownMenuProps,
  ErrorText,
  FieldContainer,
  FieldLabel,
  HelperText,
  InactivityFieldLabel,
  InactivityHeader,
  InactivityPeriodContainer,
  InactivityPeriodRow,
  InactivityPeriodUnit,
  InactivityRuleCard,
  InactivityRuleCardHeader,
  InactivitySection,
  RequiredMark,
  RuleControlContainer,
  RuleIconWrapper,
  RuleLabel,
  RuleLabelContainer,
  RuleRow,
  RuleStatus,
  SectionContainer,
  SectionHelperText,
  SectionTitle,
  SelectPlaceholder,
  StyledSelect,
  StyledTextField,
  ToggleValueText,
} from "./styles";

const RuleStatusLabel = ({ isSet, ruleKey }) => (
  <RuleStatus isSet={isSet} data-testid={`policy-engine-rule-status-${ruleKey}`}>
    {isSet ? "Set" : "Not set"}
  </RuleStatus>
);

/**
 * The group / restaurant policy form — everything below the policy-type tabs for
 * the two standard scopes. Split out of the modal so the partner benefit program
 * tab can swap the whole body rather than branch field by field.
 */
export const StandardPolicyForm = ({
  policyConfigurationData,
  setPolicyConfigurationData,
  errors,
  setErrors,
  isEditMode = false,
  activePolicyCheck = {},
  restaurantGroupDropdown = {},
  restaurantDropdown = {},
  manualUsersDropdown = {},
}) => {
  const isRestaurantPolicy = policyConfigurationData?.policy_type === "restaurant";
  const isDraft = Boolean(policyConfigurationData?.draft);
  const isActivePolicyCheckRequired = !isEditMode && !isDraft;
  const areRulesDisabled = isActivePolicyCheckRequired && !activePolicyCheck?.isClear;
  const isInactivityReviewGroupActive = hasAnyInactivityReviewField(policyConfigurationData);
  const hasExceptionsPermittedValue =
    policyConfigurationData?.exceptions_permitted !== null
    && policyConfigurationData?.exceptions_permitted !== undefined;
  const areExceptionsPermitted = policyConfigurationData?.exceptions_permitted === true;

  const updateField = (field, value) => {
    setPolicyConfigurationData((currentData) => ({ ...currentData, [field]: value }));
    setErrors((currentErrors) => ({ ...currentErrors, [field]: undefined }));
  };

  const handleRestaurantGroupChange = (event) => {
    updateField("restaurant_group_id", event.target.value);
  };

  // Switching exceptions off drops the selection: the endpoint rejects a list
  // without the switch, so leaving it behind would make the next save fail.
  const handleExceptionsPermittedChange = (isPermitted) => {
    setPolicyConfigurationData((currentData) => ({
      ...currentData,
      exceptions_permitted: isPermitted,
      exception_network_guest_ids: isPermitted ? currentData?.exception_network_guest_ids || [] : [],
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      exceptions_permitted: undefined,
      exception_network_guest_ids: undefined,
    }));
  };

  const handleInactivityCheckToggle = (checkValue) => {
    const currentChecks = policyConfigurationData?.inactivity_checks || [];
    const nextChecks = currentChecks.includes(checkValue)
      ? currentChecks.filter((check) => check !== checkValue)
      : [...currentChecks, checkValue];

    updateField("inactivity_checks", nextChecks);
  };

  const handleAddInactivityRule = () => {
    setPolicyConfigurationData((currentData) => ({
      ...currentData,
      inactivity_rule_enabled: true,
      inactivity_checks: currentData?.inactivity_checks?.length
        ? currentData.inactivity_checks
        : DEFAULT_INACTIVITY_CHECKS,
      inactivity_period_days: isIntegerFieldSet(currentData?.inactivity_period_days)
        ? currentData.inactivity_period_days
        : DEFAULT_INACTIVITY_PERIOD_DAYS,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      inactivity_checks: undefined,
      inactivity_period_days: undefined,
    }));
  };

  const handleRemoveInactivityRule = () => {
    setPolicyConfigurationData((currentData) => ({
      ...currentData,
      inactivity_rule_enabled: false,
      inactivity_checks: [],
      inactivity_period_days: "",
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      inactivity_checks: undefined,
      inactivity_period_days: undefined,
    }));
  };

  // In edit mode the scope is locked, and its name is handed in already resolved
  // by the listing — the create-scoped dropdowns are not even queried, so their
  // caches cannot be relied on to hold the policy's own scope.
  const getRestaurantGroupName = (restaurantGroupId) => {
    if (isEditMode) {
      return policyConfigurationData?.scope_name || "";
    }

    const restaurantGroup = restaurantGroupDropdown?.optionsCache?.find(
      (option) => String(option?.id) === String(restaurantGroupId)
    );
    return restaurantGroup?.name || "";
  };

  const getSelectedRestaurantNames = () => {
    if (isEditMode) {
      return policyConfigurationData?.scope_name || "";
    }

    const selectedIds = policyConfigurationData?.restaurant_ids || [];
    const names = selectedIds.map((restaurantId) => {
      const restaurant = restaurantDropdown?.optionsCache?.find(
        (option) => String(option?.id) === String(restaurantId)
      );
      return restaurant?.name || restaurantId;
    });

    return names.join(", ");
  };

  // Both lists are consulted: the cache accumulates across pages and searches,
  // while `options` holds what is on screen right now — a user just clicked is in
  // the latter even if the cache effect has not flushed yet.
  const findManualUserName = (guestId) => {
    const matches = (option) => String(option?.id) === String(guestId);
    const guest = manualUsersDropdown?.optionsCache?.find(matches)
      || manualUsersDropdown?.options?.find(matches);

    return guest?.name;
  };

  // One selection reads as the user's name; several collapse to a count, since a
  // joined list of names overflows the field long before it stays legible.
  const getSelectedExceptionUsersLabel = () => {
    const selectedIds = policyConfigurationData?.exception_network_guest_ids || [];

    if (selectedIds.length > 1) {
      return `${selectedIds.length} selected`;
    }

    // Falls back to the raw id only when the name is genuinely unresolvable, so a
    // selection is never silently invisible.
    return selectedIds.length ? findManualUserName(selectedIds[0]) || `${selectedIds[0]}` : "";
  };

  return (
    <>
      <SectionContainer>
        {/* The two scopes are mutually exclusive: a group policy is scoped by its
            group, a restaurant policy by its restaurants. Only one filter shows. */}
        {!isRestaurantPolicy && (
          <FieldContainer>
            <FieldLabel>Restaurant group<RequiredMark>*</RequiredMark></FieldLabel>
            <FormControl fullWidth size="small">
              <StyledSelect
                data-testid="policy-engine-modal-restaurant-group-select"
                size="small"
                displayEmpty
                disabled={isEditMode}
                value={policyConfigurationData?.restaurant_group_id || ""}
                renderValue={() => {
                  const name = getRestaurantGroupName(policyConfigurationData?.restaurant_group_id);
                  return name || <SelectPlaceholder>Select a group...</SelectPlaceholder>;
                }}
                onChange={handleRestaurantGroupChange}
                onClose={restaurantGroupDropdown?.onMenuClose}
                error={!!errors?.restaurant_group_id}
                MenuProps={dropdownMenuProps(restaurantGroupDropdown?.menuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={restaurantGroupDropdown?.onSearchChange}
                  placeholder="Search restaurant group"
                  isFetching={restaurantGroupDropdown?.isFetching}
                />
                {restaurantGroupDropdown?.options?.map((option) => (
                  <StyledMenuItem key={option?.id} value={option?.id}>
                    {String(policyConfigurationData?.restaurant_group_id) === String(option?.id)
                      ? <SelectedMenuItem data={option?.name} />
                      : option?.name}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={restaurantGroupDropdown?.hasNextPage}
                  targetRef={restaurantGroupDropdown?.loaderRef}
                />
              </StyledSelect>
            </FormControl>
            {!!errors?.restaurant_group_id && <ErrorText>{errors.restaurant_group_id}</ErrorText>}
          </FieldContainer>
        )}

        {isRestaurantPolicy && (
          <FieldContainer>
            <FieldLabel>Restaurant<RequiredMark>*</RequiredMark></FieldLabel>
            <FormControl fullWidth size="small">
              <StyledSelect
                data-testid="policy-engine-modal-restaurant-select"
                size="small"
                multiple
                displayEmpty
                disabled={isEditMode}
                value={policyConfigurationData?.restaurant_ids || []}
                renderValue={() => {
                  const names = getSelectedRestaurantNames();
                  return names || <SelectPlaceholder>Select a restaurant...</SelectPlaceholder>;
                }}
                onChange={(event) => updateField("restaurant_ids", event.target.value)}
                onClose={restaurantDropdown?.onMenuClose}
                error={!!errors?.restaurant_ids}
                MenuProps={dropdownMenuProps(restaurantDropdown?.menuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={restaurantDropdown?.onSearchChange}
                  placeholder="Search restaurant"
                  isFetching={restaurantDropdown?.isFetching}
                />
                {restaurantDropdown?.options?.map((option) => (
                  <StyledMenuItem key={option?.id} value={option?.id}>
                    {policyConfigurationData?.restaurant_ids?.includes(option?.id)
                      ? <SelectedMenuItem data={option?.name} />
                      : option?.name}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={restaurantDropdown?.hasNextPage}
                  targetRef={restaurantDropdown?.loaderRef}
                />
              </StyledSelect>
            </FormControl>
            {!!errors?.restaurant_ids && <ErrorText>{errors.restaurant_ids}</ErrorText>}
            <HelperText>Fields left unset inherit from the parent policy.</HelperText>
          </FieldContainer>
        )}

        {/* Outcome of the check on the selected scope. A conflict is reported
            in the snackbar by the page, so only these two states show here. */}
        {isActivePolicyCheckRequired && !!activePolicyCheck?.isChecking && (
          <ActivePolicyCheckText data-testid="policy-engine-active-policy-checking">
            {ACTIVE_POLICY_CHECKING_TEXT}
          </ActivePolicyCheckText>
        )}
        {isActivePolicyCheckRequired && !activePolicyCheck?.isChecking && !!activePolicyCheck?.isClear && (
          <ActivePolicyCheckText isClear data-testid="policy-engine-active-policy-clear">
            {ACTIVE_POLICY_CLEAR_TEXT}
          </ActivePolicyCheckText>
        )}

        <FieldContainer sx={{ marginBottom: 0 }}>
          <FieldLabel>Description</FieldLabel>
          <StyledTextField
            inputProps={{ "data-testid": "policy-engine-description-input" }}
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Description of this policy"
            value={policyConfigurationData?.description || ""}
            onChange={(event) => updateField("description", event.target.value)}
          />
        </FieldContainer>
      </SectionContainer>

      {/* Effective rules */}
      <SectionContainer>
        <SectionTitle>Effective rules</SectionTitle>
        <SectionHelperText>Fields you set here override the parent group policy.</SectionHelperText>

        <RuleRow>
          <RuleLabelContainer>
            <RuleIconWrapper><PersonOutlineIcon /></RuleIconWrapper>
            <RuleLabel>Applies to<RequiredMark>*</RequiredMark></RuleLabel>
          </RuleLabelContainer>
          <RuleControlContainer>
            <FormControl size="small" sx={{ width: "150px" }}>
              <StyledSelect
                data-testid="policy-engine-applies-to-select"
                size="small"
                disabled
                value={policyConfigurationData?.verification_method || "manual"}
              >
                {APPLIES_TO_OPTIONS.map((option) => (
                  <StyledMenuItem key={option.value} value={option.value}>
                    {option.label}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </FormControl>
            <RuleStatusLabel isSet={!!policyConfigurationData?.verification_method} ruleKey="verification_method" />
          </RuleControlContainer>
        </RuleRow>

        <RuleRow>
          <RuleLabelContainer>
            <RuleIconWrapper><VerifiedUserOutlinedIcon /></RuleIconWrapper>
            <RuleLabel>Verification method{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}</RuleLabel>
          </RuleLabelContainer>
          <RuleControlContainer>
            <FormControl size="small" sx={{ width: "150px" }}>
              <StyledSelect
                data-testid="policy-engine-notification-recipients-select"
                size="small"
                displayEmpty
                disabled={areRulesDisabled}
                value={policyConfigurationData?.notification_recipients || ""}
                renderValue={(selected) => {
                  const option = NOTIFICATION_RECIPIENT_OPTIONS.find((item) => item.value === selected);
                  return option?.label || "Inherit";
                }}
                onChange={(event) => updateField("notification_recipients", event.target.value)}
                error={!!errors?.notification_recipients}
              >
                <StyledMenuItem value="">Inherit</StyledMenuItem>
                {NOTIFICATION_RECIPIENT_OPTIONS.map((option) => (
                  <StyledMenuItem key={option.value} value={option.value}>
                    {policyConfigurationData?.notification_recipients === option.value
                      ? <SelectedMenuItem data={option.label} />
                      : option.label}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </FormControl>
            <RuleStatusLabel isSet={!!policyConfigurationData?.notification_recipients} ruleKey="notification_recipients" />
          </RuleControlContainer>
        </RuleRow>

        <RuleRow>
          <RuleLabelContainer>
            <RuleIconWrapper><AccessTimeIcon /></RuleIconWrapper>
            <RuleLabel>Verification frequency{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}</RuleLabel>
          </RuleLabelContainer>
          <RuleControlContainer>
            <FormControl size="small" sx={{ width: "150px" }}>
              <StyledSelect
                data-testid="policy-engine-verification-frequency-select"
                size="small"
                displayEmpty
                disabled={areRulesDisabled}
                value={policyConfigurationData?.verification_frequency || ""}
                renderValue={(selected) => {
                  const option = VERIFICATION_FREQUENCY_OPTIONS.find((item) => item.value === selected);
                  return option?.label || "Inherit";
                }}
                MenuProps={{
                  PaperProps: { sx: { width: "200px" } },
                }}
                onChange={(event) => updateField("verification_frequency", event.target.value)}
                error={!!errors?.verification_frequency}
              >
                <StyledMenuItem value="">Inherit</StyledMenuItem>
                {VERIFICATION_FREQUENCY_OPTIONS.map((option) => (
                  <StyledMenuItem key={option.value} value={option.value}>
                    {policyConfigurationData?.verification_frequency === option.value
                      ? <SelectedMenuItem data={option.label} />
                      : option.label}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </FormControl>
            <RuleStatusLabel isSet={!!policyConfigurationData?.verification_frequency} ruleKey="verification_frequency" />
          </RuleControlContainer>
        </RuleRow>

        <RuleRow>
          <RuleLabelContainer>
            <RuleIconWrapper><PeopleOutlineIcon /></RuleIconWrapper>
            <RuleLabel>Max active users</RuleLabel>
          </RuleLabelContainer>
          <RuleControlContainer>
            <StyledTextField
              size="small"
              sx={{ width: "150px" }}
              placeholder="100"
              disabled={areRulesDisabled}
              inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-max-active-users-input" }}
              value={policyConfigurationData?.max_active_users ?? ""}
              onChange={(event) => updateField("max_active_users", sanitizeIntegerInput(event.target.value))}
            />
            <RuleStatusLabel isSet={isIntegerFieldSet(policyConfigurationData?.max_active_users)} ruleKey="max_active_users" />
          </RuleControlContainer>
        </RuleRow>

        <RuleRow>
          <RuleLabelContainer>
            <RuleIconWrapper><ErrorOutlineIcon /></RuleIconWrapper>
            <RuleLabel>Exceptions permitted</RuleLabel>
          </RuleLabelContainer>
          <RuleControlContainer>
            <Box sx={{ alignItems: "center", display: "flex", gap: "4px", width: "150px" }}>
              <StyledSwitch
                data-testid="policy-engine-exceptions-permitted-switch"
                disabled={areRulesDisabled}
                checked={policyConfigurationData?.exceptions_permitted === true}
                onChange={(event) => handleExceptionsPermittedChange(event.target.checked)}
              />
              <ToggleValueText>
                {policyConfigurationData?.exceptions_permitted === true ? "Allowed" : "Not allowed"}
              </ToggleValueText>
            </Box>
            <RuleStatusLabel isSet={hasExceptionsPermittedValue} ruleKey="exceptions_permitted" />
          </RuleControlContainer>
        </RuleRow>

        <RuleRow>
          <RuleLabelContainer>
            <RuleIconWrapper><PersonOutlineIcon /></RuleIconWrapper>
            <RuleLabel>Excepted users{areExceptionsPermitted && <RequiredMark>*</RequiredMark>}</RuleLabel>
          </RuleLabelContainer>
          <RuleControlContainer>
            <Box sx={{ width: "150px" }}>
              <FormControl fullWidth size="small">
                <StyledSelect
                  data-testid="policy-engine-exception-users-select"
                  size="small"
                  multiple
                  displayEmpty
                  disabled={areRulesDisabled || !areExceptionsPermitted}
                  value={policyConfigurationData?.exception_network_guest_ids || []}
                  renderValue={() => {
                    const label = getSelectedExceptionUsersLabel();
                    return label || <SelectPlaceholder>Select users</SelectPlaceholder>;
                  }}
                  onChange={(event) => updateField("exception_network_guest_ids", event.target.value)}
                  onClose={manualUsersDropdown?.onMenuClose}
                  error={!!errors?.exception_network_guest_ids}
                  MenuProps={dropdownMenuProps(manualUsersDropdown?.menuRef)}
                >
                  <DropdownSearchBar
                    changeSearchText={manualUsersDropdown?.onSearchChange}
                    placeholder="Search user"
                    isFetching={manualUsersDropdown?.isFetching}
                  />
                  {manualUsersDropdown?.options?.map((option) => (
                    <StyledMenuItem key={option?.id} value={option?.id}>
                      {policyConfigurationData?.exception_network_guest_ids?.includes(option?.id)
                        ? <SelectedMenuItem data={option?.name} />
                        : option?.name}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={manualUsersDropdown?.hasNextPage}
                    targetRef={manualUsersDropdown?.loaderRef}
                  />
                </StyledSelect>
              </FormControl>
            </Box>
            <RuleStatusLabel
              isSet={!!policyConfigurationData?.exception_network_guest_ids?.length}
              ruleKey="exception_network_guest_ids"
            />
          </RuleControlContainer>
        </RuleRow>
        <RuleRow>
          <RuleLabelContainer>
            <RuleIconWrapper><HourglassEmptyIcon /></RuleIconWrapper>
            <RuleLabel>Grace period (days){isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}</RuleLabel>
          </RuleLabelContainer>
          <RuleControlContainer>
            <Box sx={{ width: "150px" }}>
              <StyledTextField
                fullWidth
                size="small"
                placeholder="7"
                disabled={areRulesDisabled}
                inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-grace-period-input" }}
                value={policyConfigurationData?.grace_period_days ?? ""}
                onChange={(event) => updateField("grace_period_days", sanitizeIntegerInput(event.target.value))}
                error={!!errors?.grace_period_days}
              />
            </Box>
            <RuleStatusLabel isSet={isIntegerFieldSet(policyConfigurationData?.grace_period_days)} ruleKey="grace_period_days" />
          </RuleControlContainer>
        </RuleRow>
      </SectionContainer>

      {/* Inactivity rules */}
      <InactivitySection>
        <InactivityHeader>
          <Box>
            <SectionTitle>
              Inactivity rules{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}
            </SectionTitle>
            <SectionHelperText sx={{ marginBottom: 0 }}>
              What counts as activity for a user.
            </SectionHelperText>
          </Box>
          {!policyConfigurationData?.inactivity_rule_enabled && (
            <AddRuleButton
              data-testid="policy-engine-add-inactivity-rule-btn"
              variant="outlined"
              disableRipple
              disabled={areRulesDisabled}
              startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
              onClick={handleAddInactivityRule}
            >
              Add rule
            </AddRuleButton>
          )}
        </InactivityHeader>

        {!!policyConfigurationData?.inactivity_rule_enabled && (
          <InactivityRuleCard data-testid="policy-engine-inactivity-rule-card">
            <Box>
              <InactivityRuleCardHeader>
                <InactivityFieldLabel sx={{ marginBottom: 0 }}>
                  Counts as activity{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}
                </InactivityFieldLabel>
                <CloseIconButton
                  data-testid="policy-engine-remove-inactivity-rule-btn"
                  onClick={handleRemoveInactivityRule}
                  disableRipple
                  disabled={areRulesDisabled}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </CloseIconButton>
              </InactivityRuleCardHeader>
              <ActivityChipGroup>
                {INACTIVITY_CHECK_OPTIONS.map((option) => {
                  const isSelected = policyConfigurationData?.inactivity_checks?.includes(option.value);

                  return (
                    <ActivityChip
                      key={option.value}
                      data-testid={`policy-engine-inactivity-check-${option.value}`}
                      isSelected={isSelected}
                      hasError={!!errors?.inactivity_checks}
                      sx={areRulesDisabled ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
                      onClick={() => !areRulesDisabled && handleInactivityCheckToggle(option.value)}
                    >
                      <ActivityChipCheckbox
                        checked={Boolean(isSelected)}
                        disabled={areRulesDisabled}
                        disableRipple
                        tabIndex={-1}
                        inputProps={{ "aria-label": option.label }}
                      />
                      {option.label}
                    </ActivityChip>
                  );
                })}
              </ActivityChipGroup>
            </Box>

            <InactivityPeriodContainer>
              <InactivityFieldLabel>Inactivity period{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}</InactivityFieldLabel>
              <InactivityPeriodRow>
                <StyledTextField
                  size="small"
                  sx={{ width: "80px" }}
                  placeholder="30"
                  disabled={areRulesDisabled}
                  inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-inactivity-period-input" }}
                  value={policyConfigurationData?.inactivity_period_days ?? ""}
                  onChange={(event) => updateField("inactivity_period_days", sanitizeIntegerInput(event.target.value))}
                  error={!!errors?.inactivity_period_days}
                />
                <InactivityPeriodUnit>days</InactivityPeriodUnit>
              </InactivityPeriodRow>
            </InactivityPeriodContainer>
          </InactivityRuleCard>
        )}
      </InactivitySection>
    </>
  );
};
